import React, {
    useState,
    useEffect,
    useCallback,
    useContext
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { ServiceManager } from '@jutro/services';
import { withRouter } from 'react-router-dom';
import {
    Loader, ModalNextProvider, Icon
} from '@jutro/components';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { IntlContext, TranslatorContext } from '@jutro/locale';
import { Claim } from 'gw-capability-fnol-common-react';
import { LobIconUtil } from 'gw-portals-util-js';
import { getNormalizedLOBName } from 'gw-portals-config-js';

import { useValidation } from 'gw-portals-validation-react';

import { useAuthentication } from 'gw-digital-auth-react';
import FNOLPolicySearchModal from '../../components/FNOLPolicySearchModal/FNOLPolicySearchModal';
import styles from './FNOLSelectPolicy.module.scss';
import metadata from './FNOLSelectPolicy.metadata.json5';
import messages from './FNOLSelectPolicy.messages';

function FNOLSelectPolicy(props) {
    const {
        onValidate,
        isComponentValid,
        registerComponentValidation,
    } = useValidation('FNOLSelectPolicy');
    const {
        wizardData: claimVM,
        history,
        steps,
        goNext,
        basePath,
        updateWizardData,
        history: {
            location: { state }
        }
    } = props;

    const {
        redirectPath,
        claimNumber,
        claimStatus
    } = state || {};

    const translator = useContext(TranslatorContext);
    const { FNOLService } = useDependencies('FNOLService');
    const { ClaimService } = useDependencies('ClaimService');

    const [isLoading, setIsLoading] = useState(true);
    const [policyNumberFromFlow, sePolicyNumberFromFlow] = useState('');
    const [hasFlowBeenEntered, setHasFlowBeenEntered] = useState(false);
    const [hasSearchBeenTriggered, setHasSearchBeenTriggered] = useState(false);
    const [policyList, setPolicyList] = useState([]);
    const [policySearchCriteriaVM, setPolicySearchCriteriaVM] = useState({});

    const intl = useContext(IntlContext);
    const viewModelService = useContext(ViewModelServiceContext);

    const { refreshData } = useDataRefresh();
    const { authHeader } = useAuthentication();

    const isClaimSelectionValid = useCallback(() => {
        const test = !(
            !claimVM.lossDate.aspects.valid
            || !claimVM.policy.aspects.valid
            || !claimVM.policy.aspects.subtreeValid
            || _.isEmpty(claimVM.policy.value)
            || !(new Date(claimVM.value.lossDate) > new Date(claimVM.policy.value.effectiveDate))
            || !(new Date(claimVM.value.lossDate) <= new Date(claimVM.policy.value.expirationDate))
        );
        return test;
    }, [claimVM.lossDate, claimVM.policy]);

    useEffect(() => {
        if (!_.isEmpty(claimVM.value, 'policy') && !_.isEmpty(claimVM.value.policy, 'policyNumber')) {
            setHasFlowBeenEntered(true);
            sePolicyNumberFromFlow(_.get(claimVM.value, 'policy.policyNumber'));
        }

        const localeService = ServiceManager.getService('locale-service');
        setPolicySearchCriteriaVM(viewModelService.create(
            { country: localeService.getDefaultCountryCode() }, 'cc',
            'edgev1141.capabilities.claim.fnol.policy.dto.PolicySearchCriteriaDTO'
        ));
        setIsLoading(false);
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        registerComponentValidation(isClaimSelectionValid);
    }, [isClaimSelectionValid, registerComponentValidation]);

    const redirectDraftClaimFlow = useCallback(() => {
        setIsLoading(true);
        if (_.isFunction(ClaimService.addRecentlyViewedClaim)) {
            ClaimService.addRecentlyViewedClaim(claimNumber, authHeader);
        }
        return FNOLService.getClaim(claimNumber, authHeader).then((getClaimResponse) => {
            claimVM.value = getClaimResponse;
            const normalizedLobCode = getNormalizedLOBName(claimVM.value.policy.policyType);
            return history.push({
                pathname: `/fnol-${normalizedLobCode}`,
                state: {
                    claimData: getClaimResponse,
                    redirectPath: redirectPath,
                    claimStatus: claimStatus
                }
            });
        });
    }, [
        ClaimService,
        claimVM,
        claimNumber,
        authHeader,
        FNOLService,
        history,
        redirectPath,
        claimStatus
    ]);

    useEffect(() => {
        if (!_.isEmpty(claimNumber)) {
            redirectDraftClaimFlow();
        }
        const stepsWithoutSelectPolicy = steps.slice(1, steps.length);
        if (
            basePath !== '/fnol-select-policy'
            && !stepsWithoutSelectPolicy.some((step) => step.visited)
        ) {
            goNext();
        }
        // This will call only once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handlePolicySelect = useCallback((selectedPolicyNumber) => {
        const policy = _.find(policyList, { policyNumber: selectedPolicyNumber });
        if (hasFlowBeenEntered) {
            if (policyNumberFromFlow !== selectedPolicyNumber) {
                ModalNextProvider.showConfirm({
                    title: messages.changePolicyModalTitle,
                    message: messages.changePolicyModalMessage,
                    status: 'warning',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.changePolicyModalConfirmationButtonText,
                    cancelButtonText: messages.changePolicyModalCancelButtonText
                    // eslint-disable-next-line consistent-return
                }).then((results) => {
                    debugger;
                    if (results === 'cancel') {
                        return false;
                    }
                    claimVM.value = new Claim();
                    _.set(claimVM, 'policy.value', policy);
                    updateWizardData(claimVM);
                }).catch(_.noop);
            }
        } else {
            _.set(claimVM, 'policy.value', policy);
            updateWizardData(claimVM);
        }
    },
    [claimVM, hasFlowBeenEntered, policyList, policyNumberFromFlow, updateWizardData]);

    const handleDateOfLossChange = useCallback((dateOfLoss) => {
        _.set(claimVM, 'lossDate', dateOfLoss);
    },
    [claimVM]);

    const searchForAPolicy = useCallback(() => {
        let policySearchDto = policySearchCriteriaVM.value
        policySearchDto.lossDate = policySearchDto.lossDate || getDefaultDateOfLoss();
        return FNOLService.searchPolicies(policySearchDto, authHeader)
            .then((policies) => {
                if (policies.length === 1) {
                    if (!claimVM.policy.value) {
                        const policyExpiryDate = new Date(policies[0].expirationDate);
                        if (policyExpiryDate > new Date()){
                            const nowString = new Date().toISOString().split('.')[0]+"Z";
                            policySearchDto.lossDate = nowString
                            _.set(claimVM, 'lossDate', nowString);
                        }else{
                            policySearchDto.lossDate =  policies[0].expirationDate;
                            _.set(claimVM, 'lossDate', policies[0].expirationDate);
                        }
                        _.set(claimVM, 'policy.value', policies[0]);
                    }
                }
                setHasSearchBeenTriggered(true);
                setPolicyList(policies);
            })
            .catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.policySearchErrorTitle,
                    message: messages.policySearchErrorText,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: messages.policySearchConfirmButtonText
                }).catch(_.noop);
                return false;
            });
    }, [FNOLService, authHeader, claimVM, policySearchCriteriaVM.value]);

    const searchPolicyBySearchCriteria = useCallback(async () => {
        _.set(policySearchCriteriaVM.value, 'policyNumber', undefined);
        await searchForAPolicy();
    }, [policySearchCriteriaVM.value, searchForAPolicy]);

    const searchPolicyByPolicyNumber = useCallback(async () => {
        const keywords = ['firstName', 'lastName', 'city', 'state', 'zip', 'policyType'];
        _.each(keywords, (keyword) => _.set(policySearchCriteriaVM.value, keyword, undefined));
        await searchForAPolicy();
    }, [policySearchCriteriaVM.value, searchForAPolicy]);

    const getFormattedDate = useCallback((items, index, property) => {
        return intl.formatDate(new Date(items[property.id]), { year: 'numeric', month: 'short', day: 'numeric' });
    }, [intl]);

    const getProductImage = useCallback((item) => {
        const icon = LobIconUtil.getFontIcon(item.policyType);
        return (
            <Icon
                icon={icon}
                title={item.policyType}
            />
        );
    }, []);

    const getCell = useCallback((item, index, property) => {
        return <span>{item[property.id]}</span>;
    }, []);

    const handlePolicyCriteriaVMChange = useCallback((newModel) => {
        debugger;
        setPolicySearchCriteriaVM(newModel);
        const lossDate = _.get(claimVM.value, 'lossDate');
        if(newModel.lossDate.value && (lossDate !== newModel.lossDate.value)){
            handleDateOfLossChange(newModel.lossDate.value);
        }
        refreshData();
    }, [refreshData]);

    const showPolicySearchModal = useCallback(() => {
        ModalNextProvider.showModal(
            <FNOLPolicySearchModal
                viewModelService={viewModelService}
                searchPolicyBySearchCriteria={searchPolicyBySearchCriteria}
                policySearchCriteriaVM={policySearchCriteriaVM}
                handlePolicyCriteriaVMChange={handlePolicyCriteriaVMChange}
            />
        );
    }, [
        handlePolicyCriteriaVMChange,
        policySearchCriteriaVM,
        searchPolicyBySearchCriteria,
        viewModelService
    ]);

    const switchToManualSearchMode = useCallback(() => {
        showPolicySearchModal();
    }, [showPolicySearchModal]);

    const generateDataForRadioButton = useCallback((row) => {
        return {
            code: row.policyNumber,
            value: ''
        };
    }, []);

    const shouldShowInfoNotification = useCallback(() => {
        return hasSearchBeenTriggered && policyList.length === 0;
    }, [hasSearchBeenTriggered, policyList.length]);

    const onNext = useCallback(() => {
        const normalizedLobCode = getNormalizedLOBName(claimVM.value.policy.policyType);
        return history.push({
            pathname: `/fnol-${normalizedLobCode}`,
            state: {
                claimData: claimVM.value,
                redirectPath: redirectPath
            }
        });
    }, [claimVM.value, history, redirectPath]);

    const isPolicyNumberValid = useCallback(() => {
        const policyNumberRegex = /^([A-Z]{2}[0-9]{8})$/;
        return !(!(_.isUndefined(policySearchCriteriaVM.value))
                && !(_.isUndefined(policySearchCriteriaVM.value.policyNumber))
                && !(_.isEmpty(policySearchCriteriaVM.value.policyNumber))
                && policyNumberRegex.test(policySearchCriteriaVM.value.policyNumber));
    }, [policySearchCriteriaVM]);

    const isNextButtonDisabled = useCallback(() => {
        let isPolicyAndModelSame = false;
        if (!(_.isUndefined(policySearchCriteriaVM.value))
            && !(_.isUndefined(policySearchCriteriaVM.value.policyNumber))
            && !(_.isUndefined(claimVM.policy))
            && !(_.isUndefined(claimVM.policy.value))
            && !(_.isUndefined(claimVM.policy.value.policyNumber))) {
            isPolicyAndModelSame = (policySearchCriteriaVM.value.policyNumber === claimVM.policy.value.policyNumber);
        }
        return !(isPolicyAndModelSame && isComponentValid);
    }, [policySearchCriteriaVM, claimVM, isComponentValid]);

    const getDefaultDateOfLoss = () => {
        if (policySearchCriteriaVM.value && policySearchCriteriaVM.value.lossDate) {
            return policySearchCriteriaVM.value.lossDate.split('.')[0]+"Z";
        }
        if (claimVM.value && claimVM.value.lossDate) {
            return claimVM.value.lossDate.split('.')[0];
        }
        return (new Date().toISOString().split('.')[0]+"Z");
    };

    const overrides = {
        noPoliciesFoundNotification: {
            visible: shouldShowInfoNotification()
        },
        lossDateDiv:{
            visible: _.get(claimVM.value, 'policy.policyNumber')  !== undefined 
        },
        whenDidLossOccur: {
            maxDate: _.get(claimVM.value, 'policy.expirationDate') ? new Date(_.get(claimVM.value, 'policy.expirationDate')) : new Date(),
            minDate: new Date(_.get(claimVM.value, 'policy.effectiveDate')),
            defaultValue: getDefaultDateOfLoss()
           // onValueChange: handleDateOfLossChange
        },
        policyTable: {
            data: policyList,
            visible: policyList.length > 0
        },
        policySelectRadio: {
            option: generateDataForRadioButton,
            value: (!_.isEmpty(claimVM.value) && _.get(claimVM.value, 'policy.policyNumber')),
            onValueChange: handlePolicySelect
        },
        policyNumber: {
            defaultValue: (!_.isEmpty(claimVM.value) && _.get(claimVM.value, 'policy.policyNumber')),
        },
        search: {
            disabled: isPolicyNumberValid()
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            switchToManualSearchMode: switchToManualSearchMode,
            getFormattedDate: getFormattedDate,
            getProductImage: getProductImage,
            getCell: getCell,
            onSearchClicked: searchPolicyByPolicyNumber,
        }
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <WizardPage
            showPrevious={false}
            disableNext={isNextButtonDisabled()}
            onNext={onNext}
            nextButtonID={translator(messages.fnolPolicySearchNextButton)}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={policySearchCriteriaVM}
                onModelChange={handlePolicyCriteriaVMChange}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

FNOLSelectPolicy.propTypes = {
    ...wizardProps,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};

export default withRouter(FNOLSelectPolicy);
